import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Tabs from '../../components/ui/Tabs';
import Card from '../../components/ui/Card';
import Select from '../../components/ui/Select';
import DatePicker from '../../components/ui/DatePicker';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import Can from '../../auth/Can';
import { fetchReport, exportReport } from '../../api/reports';
import { listWorkflowCategories } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';
import { saveBlobResponse } from '../../utils/download';
import FunnelView from './views/FunnelView';
import RejectionsView from './views/RejectionsView';
import TimeInStepView from './views/TimeInStepView';
import AgentPerformanceView from './views/AgentPerformanceView';
import ExpiryWatchlistView from './views/ExpiryWatchlistView';

const REPORT_TABS = [
  { value: 'funnel', label: 'Recruitment Funnel' },
  { value: 'rejections', label: 'Rejection Analysis' },
  { value: 'time-in-step', label: 'Time in Step' },
  { value: 'agent-performance', label: 'Agent Performance' },
  { value: 'expiry-watchlist', label: 'Expiry Watchlist' },
];

export default function Reports() {
  const [type, setType] = useState('funnel');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState('');
  const [days, setDays] = useState('60');

  const categoriesQuery = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });

  const params =
    type === 'expiry-watchlist'
      ? { days: days || 60 }
      : {
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          category_id: categoryId || undefined,
          status: status || undefined,
        };

  const reportQuery = useQuery({ queryKey: ['reports', type, params], queryFn: () => fetchReport(type, params) });

  const handleExport = async () => {
    try {
      const response = await exportReport(type, params);
      saveBlobResponse(response, `report-${type}.csv`);
    } catch {
      pushToast({ type: 'error', message: 'Could not export report.' });
    }
  };

  return (
    <div>
      <PageHeader
        title="Reports"
        actions={
          <Can perm="candidates.export">
            <Button variant="secondary" onClick={handleExport}>
              Export CSV
            </Button>
          </Can>
        }
      />

      <Tabs tabs={REPORT_TABS} active={type} onChange={setType} />

      <div className="my-4 flex flex-wrap gap-3">
        {type === 'expiry-watchlist' ? (
          <Input
            label="Within days"
            type="number"
            min="1"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="w-32"
          />
        ) : (
          <>
            <DatePicker label="From" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
            <DatePicker label="To" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
            <Select label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-56">
              <option value="">All categories</option>
              {(categoriesQuery.data || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-44">
              <option value="">All statuses</option>
              <option value="registered">Registered</option>
              <option value="in_progress">In Progress</option>
              <option value="hired">Hired</option>
              <option value="rejected">Rejected</option>
              <option value="withdrawn">Withdrawn</option>
            </Select>
          </>
        )}
      </div>

      <Card>
        {reportQuery.isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : reportQuery.isError ? (
          <p className="text-sm text-red-600">Could not load this report. Please try again.</p>
        ) : (
          <>
            {type === 'funnel' && <FunnelView data={reportQuery.data} />}
            {type === 'rejections' && <RejectionsView data={reportQuery.data} />}
            {type === 'time-in-step' && <TimeInStepView data={reportQuery.data} />}
            {type === 'agent-performance' && <AgentPerformanceView data={reportQuery.data} />}
            {type === 'expiry-watchlist' && <ExpiryWatchlistView data={reportQuery.data} />}
          </>
        )}
      </Card>
    </div>
  );
}
